import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore,
  Build,
  Science,
  Assignment,
  Schedule,
  CheckCircle,
  RadioButtonUnchecked,
  Settings,
  Straighten,
  Speed,
  Thermostat,
  Scale,
  Timer,
  Warning,
  Info,
  Save,
  Add,
  Delete,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';

interface PreparationStep {
  id: string;
  name: string;
  description: string;
  completed: boolean;
  duration_minutes?: number;
  equipment_used?: string;
  parameters?: Record<string, any>;
  notes?: string;
  quality_check?: boolean;
}

interface PreparationFormData {
  // Sample Information
  sample_id: string;
  poids_initial_kg: number;
  
  // Crushing Stage
  crushing_method: string;
  crusher_model?: string;
  input_size_mm?: number;
  output_size_mm?: number;
  crushing_time_minutes?: number;
  crushing_temperature?: number;
  sample_splits_taken?: number;
  contamination_prevention?: string[];
  equipment_cleaning_confirmed?: boolean;
  
  // Grinding/Milling Stage
  grinding_method: string;
  mill_type?: string;
  grinding_time_minutes?: number;
  final_size_microns?: number;
  pulp_density?: number;
  grinding_media?: string;
  homogenization_method?: string;
  sub_samples_created?: number;
  storage_container_type?: string;
  storage_container_id?: string;
  
  // Quality Checks
  particle_size_verified?: boolean;
  sample_loss_percentage?: number;
  contamination_check_passed?: boolean;
  equipment_calibration_status?: string;
  moisture_content_before?: number;
  moisture_content_after?: number;
  
  // Process Steps
  preparation_steps: PreparationStep[];
  active_step: number;
  
  // Personnel & Equipment
  technician_id: string;
  equipment_ids: string[];
  start_time?: Date;
  end_time?: Date;
  
  // Storage & Location
  storage_location: string;
  storage_conditions?: {
    temperature?: number;
    humidity?: number;
    container_type?: string;
  };
  
  // Documentation
  process_notes?: string;
  quality_notes?: string;
  photos?: string[];
  certificates?: string[];
}

interface PreparationFormProps {
  initialData?: Partial<PreparationFormData>;
  onSubmit: (data: PreparationFormData, action: 'SAVE_DRAFT' | 'START_PREPARATION' | 'COMPLETE_PREPARATION') => void;
  onCancel: () => void;
  loading?: boolean;
  technicians: Array<{ id: string; name: string; speciality?: string }>;
  equipment: Array<{ id: string; name: string; type: string; status: string }>;
}

const PreparationForm: React.FC<PreparationFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  technicians,
  equipment
}) => {
  const defaultSteps: PreparationStep[] = [
    {
      id: 'reception',
      name: 'Réception et Vérification',
      description: 'Vérification de l\'échantillon et documentation',
      completed: false,
      duration_minutes: 15
    },
    {
      id: 'drying',
      name: 'Séchage (105°C)',
      description: 'Séchage à température contrôlée',
      completed: false,
      duration_minutes: 120
    },
    {
      id: 'primary_crushing',
      name: 'Concassage Primaire',
      description: 'Réduction de taille initiale',
      completed: false,
      duration_minutes: 30
    },
    {
      id: 'quartering',
      name: 'Quartage',
      description: 'Division représentative de l\'échantillon',
      completed: false,
      duration_minutes: 20
    },
    {
      id: 'fine_grinding',
      name: 'Broyage Fin',
      description: 'Broyage à la granulométrie finale',
      completed: false,
      duration_minutes: 45
    },
    {
      id: 'sieving',
      name: 'Tamisage Final',
      description: 'Contrôle granulométrique final',
      completed: false,
      duration_minutes: 25
    },
    {
      id: 'packaging',
      name: 'Conditionnement',
      description: 'Mise en contenants d\'analyse',
      completed: false,
      duration_minutes: 15
    },
    {
      id: 'labeling',
      name: 'Étiquetage et Stockage',
      description: 'Étiquetage et stockage sécurisé',
      completed: false,
      duration_minutes: 10
    }
  ];

  // Helper function to safely convert date values
  const safeDate = (dateValue: any): Date | undefined => {
    if (!dateValue) return undefined;
    const date = new Date(dateValue);
    return !isNaN(date.getTime()) ? date : undefined;
  };

  const [formData, setFormData] = useState<PreparationFormData>({
    sample_id: '',
    poids_initial_kg: 0,
    crushing_method: 'CONCASSEUR_MACHOIRES',
    grinding_method: 'BROYEUR_BOULETS',
    preparation_steps: defaultSteps,
    active_step: 0,
    technician_id: '',
    equipment_ids: [],
    storage_location: '',
    contamination_prevention: [],
    ...initialData,
    // Override date fields with safe date conversion
    start_time: safeDate(initialData?.start_time),
    end_time: safeDate(initialData?.end_time),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: true,
    crushing: false,
    grinding: false,
    quality: false,
    steps: false,
    equipment: true,
    storage: false
  });

  // Crushing Methods
  const crushingMethods = [
    { value: 'CONCASSEUR_MACHOIRES', label: 'Concasseur à Mâchoires' },
    { value: 'CONCASSEUR_GIRATOIRE', label: 'Concasseur Giratoire' },
    { value: 'CONCASSEUR_CONE', label: 'Concasseur à Cône' },
    { value: 'CONCASSEUR_IMPACT', label: 'Concasseur à Impact' },
    { value: 'BROYEUR_MARTEAUX', label: 'Broyeur à Marteaux' }
  ];

  // Grinding Methods
  const grindingMethods = [
    { value: 'BROYEUR_BOULETS', label: 'Broyeur à Boulets' },
    { value: 'BROYEUR_BARRES', label: 'Broyeur à Barres' },
    { value: 'BROYEUR_DISQUE', label: 'Broyeur à Disque' },
    { value: 'PULVERISATEUR', label: 'Pulvérisateur' },
    { value: 'MORTIER_AGATE', label: 'Mortier en Agate' },
    { value: 'BROYEUR_PLANETAIRE', label: 'Broyeur Planétaire' }
  ];

  // Contamination Prevention Methods
  const contaminationPreventionMethods = [
    'Nettoyage à l\'air comprimé',
    'Nettoyage à l\'eau distillée',
    'Nettoyage au solvant',
    'Passage d\'échantillon de nettoyage',
    'Inspection visuelle',
    'Changement des pièces d\'usure'
  ];

  // Storage Locations
  const storageLocations = [
    'Laboratoire Préparation - Zone A',
    'Laboratoire Préparation - Zone B',
    'Stockage Temporaire - Étagère 1',
    'Stockage Temporaire - Étagère 2',
    'Chambre Froide - Section 1',
    'Armoire Sécurisée - Niveau 1',
    'Zone Quarantaine'
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleStepComplete = (stepIndex: number) => {
    setFormData(prev => ({
      ...prev,
      preparation_steps: prev.preparation_steps.map((step, index) => 
        index === stepIndex 
          ? { ...step, completed: !step.completed }
          : step
      )
    }));
  };

  const handleStepUpdate = (stepIndex: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      preparation_steps: prev.preparation_steps.map((step, index) => 
        index === stepIndex 
          ? { ...step, [field]: value }
          : step
      )
    }));
  };

  const handleContaminationPreventionToggle = (method: string) => {
    setFormData(prev => ({
      ...prev,
      contamination_prevention: prev.contamination_prevention?.includes(method)
        ? prev.contamination_prevention.filter(m => m !== method)
        : [...(prev.contamination_prevention || []), method]
    }));
  };

  const handleEquipmentToggle = (equipmentId: string) => {
    setFormData(prev => ({
      ...prev,
      equipment_ids: prev.equipment_ids.includes(equipmentId)
        ? prev.equipment_ids.filter(id => id !== equipmentId)
        : [...prev.equipment_ids, equipmentId]
    }));
  };

  const calculateProgress = (): number => {
    const completedSteps = formData.preparation_steps.filter(step => step.completed).length;
    return (completedSteps / formData.preparation_steps.length) * 100;
  };

  const calculateTotalDuration = (): number => {
    return formData.preparation_steps.reduce((total, step) => 
      total + (step.duration_minutes || 0), 0
    );
  };

  const validateForm = (action: 'SAVE_DRAFT' | 'START_PREPARATION' | 'COMPLETE_PREPARATION'): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic validation for all actions
    if (!formData.technician_id && action !== 'SAVE_DRAFT') {
      newErrors.technician_id = 'Technicien requis';
    }
    if (formData.poids_initial_kg <= 0 && action !== 'SAVE_DRAFT') {
      newErrors.poids_initial_kg = 'Poids initial requis';
    }

    // Additional validation for starting preparation
    if (action === 'START_PREPARATION') {
      if (formData.equipment_ids.length === 0) {
        newErrors.equipment_ids = 'Au moins un équipement doit être sélectionné';
      }
    }

    // Full validation for completion
    if (action === 'COMPLETE_PREPARATION') {
      if (formData.equipment_ids.length === 0) {
        newErrors.equipment_ids = 'Au moins un équipement doit être sélectionné';
      }
      if (!formData.storage_location) {
        newErrors.storage_location = 'Localisation de stockage requise';
      }
      const completedSteps = formData.preparation_steps.filter(step => step.completed).length;
      if (completedSteps < formData.preparation_steps.length * 0.8) {
        newErrors.preparation_steps = 'Au moins 80% des étapes doivent être complétées';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (action: 'SAVE_DRAFT' | 'START_PREPARATION' | 'COMPLETE_PREPARATION') => {
    if (validateForm(action)) {
      onSubmit(formData, action);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Formulaire de Préparation Mécanique
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Préparation d'échantillon selon les standards de laboratoire minier
        </Typography>

        {/* Progress Overview */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Progression de la Préparation
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={calculateProgress()} 
              sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Progression</Typography>
                <Typography variant="h6">{Math.round(calculateProgress())}%</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Étapes Complétées</Typography>
                <Typography variant="h6">
                  {formData.preparation_steps.filter(s => s.completed).length} / {formData.preparation_steps.length}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Durée Estimée</Typography>
                <Typography variant="h6">{Math.round(calculateTotalDuration() / 60)} heures</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Échantillon</Typography>
                <Typography variant="h6">{formData.sample_id || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Accordion expanded={expandedSections.basic} onChange={() => toggleSection('basic')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Assignment sx={{ mr: 2 }} />
            <Typography variant="h6">Informations de Base</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID Échantillon"
                  value={formData.sample_id}
                  onChange={(e) => handleInputChange('sample_id', e.target.value)}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Poids Initial (kg)"
                  type="number"
                  value={formData.poids_initial_kg}
                  onChange={(e) => handleInputChange('poids_initial_kg', parseFloat(e.target.value) || 0)}
                  error={!!errors.poids_initial_kg}
                  helperText={errors.poids_initial_kg}
                  inputProps={{ step: 0.001, min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.technician_id}>
                  <InputLabel>Technicien Responsable *</InputLabel>
                  <Select
                    value={formData.technician_id}
                    onChange={(e) => handleInputChange('technician_id', e.target.value)}
                    label="Technicien Responsable *"
                  >
                    {technicians.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id}>
                        {tech.name} {tech.speciality && `(${tech.speciality})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Heure de Début"
                  value={(() => {
                    // Triple-check date validity
                    if (!formData.start_time) return null;
                    try {
                      const date = new Date(formData.start_time);
                      return !isNaN(date.getTime()) ? date : null;
                    } catch {
                      return null;
                    }
                  })()}
                  onChange={(date) => {
                    // Ensure we only set valid dates
                    try {
                      if (date && !isNaN(new Date(date).getTime())) {
                        handleInputChange('start_time', date);
                      } else {
                        handleInputChange('start_time', undefined);
                      }
                    } catch {
                      handleInputChange('start_time', undefined);
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: false, // Prevent error state from invalid dates
                      helperText: "Optionnel - Heure de début de préparation"
                    }
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Crushing Stage */}
        <Accordion expanded={expandedSections.crushing} onChange={() => toggleSection('crushing')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Build sx={{ mr: 2 }} />
            <Typography variant="h6">Étape de Concassage</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Méthode de Concassage</InputLabel>
                  <Select
                    value={formData.crushing_method}
                    onChange={(e) => handleInputChange('crushing_method', e.target.value)}
                    label="Méthode de Concassage"
                  >
                    {crushingMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Modèle de Concasseur"
                  value={formData.crusher_model || ''}
                  onChange={(e) => handleInputChange('crusher_model', e.target.value)}
                  placeholder="Ex: Retsch BB 200"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Taille d'Entrée (mm)"
                  type="number"
                  value={formData.input_size_mm || ''}
                  onChange={(e) => handleInputChange('input_size_mm', parseFloat(e.target.value) || 0)}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Taille de Sortie (mm)"
                  type="number"
                  value={formData.output_size_mm || ''}
                  onChange={(e) => handleInputChange('output_size_mm', parseFloat(e.target.value) || 0)}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Temps de Concassage (min)"
                  type="number"
                  value={formData.crushing_time_minutes || ''}
                  onChange={(e) => handleInputChange('crushing_time_minutes', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Température (°C)"
                  type="number"
                  value={formData.crushing_temperature || ''}
                  onChange={(e) => handleInputChange('crushing_temperature', parseFloat(e.target.value) || 0)}
                  helperText="Température pendant le concassage"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre de Splits Prélevés"
                  type="number"
                  value={formData.sample_splits_taken || ''}
                  onChange={(e) => handleInputChange('sample_splits_taken', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Mesures de Prévention de Contamination
                </Typography>
                <FormGroup>
                  <Grid container>
                    {contaminationPreventionMethods.map((method) => (
                      <Grid item xs={12} sm={6} key={method}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.contamination_prevention?.includes(method) || false}
                              onChange={() => handleContaminationPreventionToggle(method)}
                            />
                          }
                          label={method}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.equipment_cleaning_confirmed || false}
                      onChange={(e) => handleInputChange('equipment_cleaning_confirmed', e.target.checked)}
                    />
                  }
                  label="Nettoyage de l'équipement confirmé"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Grinding Stage */}
        <Accordion expanded={expandedSections.grinding} onChange={() => toggleSection('grinding')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Settings sx={{ mr: 2 }} />
            <Typography variant="h6">Étape de Broyage</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Méthode de Broyage</InputLabel>
                  <Select
                    value={formData.grinding_method}
                    onChange={(e) => handleInputChange('grinding_method', e.target.value)}
                    label="Méthode de Broyage"
                  >
                    {grindingMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Type de Broyeur"
                  value={formData.mill_type || ''}
                  onChange={(e) => handleInputChange('mill_type', e.target.value)}
                  placeholder="Ex: Broyeur à boulets 500ml"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Temps de Broyage (min)"
                  type="number"
                  value={formData.grinding_time_minutes || ''}
                  onChange={(e) => handleInputChange('grinding_time_minutes', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Taille Finale (μm)"
                  type="number"
                  value={formData.final_size_microns || ''}
                  onChange={(e) => handleInputChange('final_size_microns', parseFloat(e.target.value) || 0)}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Densité de Pulpe (%)"
                  type="number"
                  value={formData.pulp_density || ''}
                  onChange={(e) => handleInputChange('pulp_density', parseFloat(e.target.value) || 0)}
                  inputProps={{ step: 0.1, min: 0, max: 100 }}
                  helperText="Si broyage humide"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Média de Broyage"
                  value={formData.grinding_media || ''}
                  onChange={(e) => handleInputChange('grinding_media', e.target.value)}
                  placeholder="Ex: Billes d'acier, billes de zircone"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Méthode d'Homogénéisation"
                  value={formData.homogenization_method || ''}
                  onChange={(e) => handleInputChange('homogenization_method', e.target.value)}
                  placeholder="Ex: Mélangeur rotatif, quartage"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Sous-échantillons Créés"
                  type="number"
                  value={formData.sub_samples_created || ''}
                  onChange={(e) => handleInputChange('sub_samples_created', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Type de Contenant"
                  value={formData.storage_container_type || ''}
                  onChange={(e) => handleInputChange('storage_container_type', e.target.value)}
                  placeholder="Ex: Flacon HDPE, Sachet kraft"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ID Contenant"
                  value={formData.storage_container_id || ''}
                  onChange={(e) => handleInputChange('storage_container_id', e.target.value)}
                  placeholder="Ex: HDPE-001"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Quality Checks */}
        <Accordion expanded={expandedSections.quality} onChange={() => toggleSection('quality')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <CheckCircle sx={{ mr: 2 }} />
            <Typography variant="h6">Contrôles Qualité</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.particle_size_verified || false}
                      onChange={(e) => handleInputChange('particle_size_verified', e.target.checked)}
                    />
                  }
                  label="Taille de particule vérifiée"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.contamination_check_passed || false}
                      onChange={(e) => handleInputChange('contamination_check_passed', e.target.checked)}
                    />
                  }
                  label="Contrôle de contamination réussi"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Perte d'Échantillon (%)"
                  type="number"
                  value={formData.sample_loss_percentage || ''}
                  onChange={(e) => handleInputChange('sample_loss_percentage', parseFloat(e.target.value) || 0)}
                  inputProps={{ step: 0.01, min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Humidité Avant (%)"
                  type="number"
                  value={formData.moisture_content_before || ''}
                  onChange={(e) => handleInputChange('moisture_content_before', parseFloat(e.target.value) || 0)}
                  inputProps={{ step: 0.01, min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Humidité Après (%)"
                  type="number"
                  value={formData.moisture_content_after || ''}
                  onChange={(e) => handleInputChange('moisture_content_after', parseFloat(e.target.value) || 0)}
                  inputProps={{ step: 0.01, min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Statut de Calibration des Équipements</InputLabel>
                  <Select
                    value={formData.equipment_calibration_status || ''}
                    onChange={(e) => handleInputChange('equipment_calibration_status', e.target.value)}
                    label="Statut de Calibration des Équipements"
                  >
                    <MenuItem value="CALIBRE">Calibré</MenuItem>
                    <MenuItem value="EXPIRE">Calibration Expirée</MenuItem>
                    <MenuItem value="EN_COURS">Calibration en Cours</MenuItem>
                    <MenuItem value="NON_REQUIS">Non Requis</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes de Contrôle Qualité"
                  value={formData.quality_notes || ''}
                  onChange={(e) => handleInputChange('quality_notes', e.target.value)}
                  placeholder="Observations sur la qualité, anomalies détectées, actions correctives..."
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Preparation Steps */}
        <Accordion expanded={expandedSections.steps} onChange={() => toggleSection('steps')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Schedule sx={{ mr: 2 }} />
            <Typography variant="h6">Étapes de Préparation</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stepper orientation="vertical">
              {formData.preparation_steps.map((step, index) => (
                <Step key={step.id} active={index === formData.active_step}>
                  <StepLabel
                    StepIconComponent={() => (
                      <IconButton
                        size="small"
                        onClick={() => handleStepComplete(index)}
                        color={step.completed ? "success" : "default"}
                      >
                        {step.completed ? <CheckCircle /> : <RadioButtonUnchecked />}
                      </IconButton>
                    )}
                  >
                    <Typography variant="h6">{step.name}</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {step.description}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Durée (min)"
                          type="number"
                          size="small"
                          value={step.duration_minutes || ''}
                          onChange={(e) => handleStepUpdate(index, 'duration_minutes', parseInt(e.target.value) || 0)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Équipement Utilisé"
                          size="small"
                          value={step.equipment_used || ''}
                          onChange={(e) => handleStepUpdate(index, 'equipment_used', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={step.quality_check || false}
                              onChange={(e) => handleStepUpdate(index, 'quality_check', e.target.checked)}
                            />
                          }
                          label="Contrôle QC"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Notes"
                          size="small"
                          value={step.notes || ''}
                          onChange={(e) => handleStepUpdate(index, 'notes', e.target.value)}
                          placeholder="Observations, paramètres spécifiques..."
                        />
                      </Grid>
                    </Grid>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </AccordionDetails>
        </Accordion>

        {/* Equipment Selection */}
        <Accordion expanded={expandedSections.equipment} onChange={() => toggleSection('equipment')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Build sx={{ mr: 2 }} />
            <Typography variant="h6">Équipements Utilisés</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle1" gutterBottom>
              Sélectionner les équipements utilisés *
            </Typography>
            <FormGroup>
              <Grid container>
                {equipment.map((eq) => (
                  <Grid item xs={12} sm={6} md={4} key={eq.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.equipment_ids.includes(eq.id)}
                          onChange={() => handleEquipmentToggle(eq.id)}
                          disabled={eq.status !== 'AVAILABLE'}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{eq.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {eq.type} - {eq.status}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
            {errors.equipment_ids && (
              <Typography color="error" variant="caption">
                {errors.equipment_ids}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Storage & Location */}
        <Accordion expanded={expandedSections.storage} onChange={() => toggleSection('storage')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Assignment sx={{ mr: 2 }} />
            <Typography variant="h6">Stockage et Localisation</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.storage_location}>
                  <InputLabel>Localisation de Stockage *</InputLabel>
                  <Select
                    value={formData.storage_location}
                    onChange={(e) => handleInputChange('storage_location', e.target.value)}
                    label="Localisation de Stockage *"
                  >
                    {storageLocations.map((location) => (
                      <MenuItem key={location} value={location}>
                        {location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Heure de Fin"
                  value={(() => {
                    // Triple-check date validity
                    if (!formData.end_time) return null;
                    try {
                      const date = new Date(formData.end_time);
                      return !isNaN(date.getTime()) ? date : null;
                    } catch {
                      return null;
                    }
                  })()}
                  onChange={(date) => {
                    // Ensure we only set valid dates
                    try {
                      if (date && !isNaN(new Date(date).getTime())) {
                        handleInputChange('end_time', date);
                      } else {
                        handleInputChange('end_time', undefined);
                      }
                    } catch {
                      handleInputChange('end_time', undefined);
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: false, // Prevent error state from invalid dates
                      helperText: "Optionnel - Heure de fin de préparation"
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Température de Stockage (°C)"
                  type="number"
                  value={formData.storage_conditions?.temperature || ''}
                  onChange={(e) => handleInputChange('storage_conditions', {
                    ...formData.storage_conditions,
                    temperature: parseFloat(e.target.value) || undefined
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Humidité de Stockage (%)"
                  type="number"
                  value={formData.storage_conditions?.humidity || ''}
                  onChange={(e) => handleInputChange('storage_conditions', {
                    ...formData.storage_conditions,
                    humidity: parseFloat(e.target.value) || undefined
                  })}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Type de Contenant"
                  value={formData.storage_conditions?.container_type || ''}
                  onChange={(e) => handleInputChange('storage_conditions', {
                    ...formData.storage_conditions,
                    container_type: e.target.value
                  })}
                  placeholder="Ex: Flacon HDPE, Sachet kraft"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes de Processus"
                  value={formData.process_notes || ''}
                  onChange={(e) => handleInputChange('process_notes', e.target.value)}
                  placeholder="Notes générales sur le processus de préparation, observations, recommandations..."
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
          >
            Annuler
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => handleSubmit('SAVE_DRAFT')}
              disabled={loading}
              startIcon={<Save />}
              color="info"
            >
              Sauvegarder Brouillon
            </Button>
            
            <Button
              variant="contained"
              onClick={() => handleSubmit('START_PREPARATION')}
              disabled={loading || !formData.technician_id || formData.poids_initial_kg <= 0}
              startIcon={<Build />}
              color="primary"
            >
              Commencer Préparation
            </Button>
            
            <Button
              variant="contained"
              onClick={() => handleSubmit('COMPLETE_PREPARATION')}
              disabled={loading || calculateProgress() < 80}
              startIcon={<CheckCircle />}
              color="success"
            >
              Finaliser Préparation
            </Button>
          </Box>
        </Box>

        {/* Summary Card */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Résumé de la Préparation
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Méthode de Concassage</Typography>
                <Typography variant="body1">
                  {crushingMethods.find(m => m.value === formData.crushing_method)?.label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Méthode de Broyage</Typography>
                <Typography variant="body1">
                  {grindingMethods.find(m => m.value === formData.grinding_method)?.label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Taille Finale</Typography>
                <Typography variant="body1">
                  {formData.final_size_microns ? `${formData.final_size_microns} μm` : 'Non spécifiée'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Durée Totale</Typography>
                <Typography variant="body1">
                  {Math.round(calculateTotalDuration() / 60)} heures
                </Typography>
              </Grid>
            </Grid>
            
            {formData.equipment_ids.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Équipements Utilisés:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.equipment_ids.map((equipmentId) => {
                    const eq = equipment.find(e => e.id === equipmentId);
                    return eq ? (
                      <Chip
                        key={equipmentId}
                        label={eq.name}
                        size="small"
                        variant="outlined"
                      />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default PreparationForm;